// EXTERNAL DEPENDENCIES
import React from 'react'
import { useDispatch } from 'react-redux'

// COMPONENTS
import { ContextualAlert } from '@paypalcorp/pp-react'
import PhotoUploadSection from '@/common/components/ui/profile/photo-upload-section'

// HOOKS
import usePhotoUploading from '@/hooks/ui'
import {
  useBusinessProfilePageContent,
  useBusinessProfileReduxState,
} from '@/screens/business-profile-page/hooks'

// REDUX
import {
  updateEditedProfileDataValues,
  updateAreValuesEdited,
} from '@/redux/features'

// FPTI
import {
  EDIT_PROFILE_PANEL_EDIT_COVER_PHOTO_FPTI,
  EDIT_PROFILE_PANEL_EDIT_PROFILE_PHOTO_FPTI,
} from '../../fpti'

// STYLES
import styles from './styles.module.less'

// TYPES
import { PhotoTypes } from '@/types'

/**
 * Displays a screen allowing them to upload their profile photos and cover photos
 */
export function ProfilePhotosSection({
  setIsCoverPhotoValid,
  setIsProfilePhotoValid,
}: Readonly<{
  setIsCoverPhotoValid: (isValid: boolean) => void
  setIsProfilePhotoValid: (isValid: boolean) => void
}>) {
  const dispatch = useDispatch()
  const {
    editedProfileData: { values },
  } = useBusinessProfileReduxState()
  const isLoading = !values.businessHandle

  const getContent = useBusinessProfilePageContent()
  const coverPhotoTitle = getContent('coverPhotoTitle')
  const profilePhotoTitle = getContent('profilePhotoTitle')

  const updateImageSrc = React.useCallback(
    (photoType: PhotoTypes, value: string) => {
      dispatch(updateEditedProfileDataValues({ [photoType]: value }))
      dispatch(updateAreValuesEdited(true))
    },
    [dispatch],
  )

  const profilePhotoUploadingProps = usePhotoUploading({
    photoType: 'profilePhoto',
    photoSrc: values.profilePhoto,
    updatePhotoValidity: (_photoType, isValid) =>
      setIsProfilePhotoValid(isValid),
    updateImageSrc,
    isLoading,
  })

  const coverPhotoUploadingProps = usePhotoUploading({
    photoType: 'coverPhoto',
    photoSrc: values.coverPhoto,
    updatePhotoValidity: (_photoType, isValid) => setIsCoverPhotoValid(isValid),
    updateImageSrc,
    isLoading,
  })

  const errorMessage =
    profilePhotoUploadingProps.errorMessage ||
    coverPhotoUploadingProps.errorMessage

  return (
    <div>
      <PhotoUploadSection
        {...coverPhotoUploadingProps}
        isDisplayPhoto
        isLoading={isLoading}
        photoType={'coverPhoto'}
        sheetTitle={coverPhotoTitle}
        errorMessage={null}
        fpti={{ editFpti: EDIT_PROFILE_PANEL_EDIT_COVER_PHOTO_FPTI }}
      />
      <PhotoUploadSection
        {...profilePhotoUploadingProps}
        isLoading={isLoading}
        photoType={'profilePhoto'}
        sheetTitle={profilePhotoTitle}
        errorMessage={null}
        fpti={{ editFpti: EDIT_PROFILE_PANEL_EDIT_PROFILE_PHOTO_FPTI }}
      />
      {errorMessage ? (
        <ContextualAlert type='error' className={styles.errorMessageContainer}>
          {errorMessage}
        </ContextualAlert>
      ) : null}
    </div>
  )
}
